// Browser-side WebRTC peer used by the public /test media demo.
// Drives a real RTCPeerConnection through the KonexZero signaling proxy.
// Mirrors test/prod/src/tier2/peer.runtime.ts but exported as a proper class
// rather than installed on `window`.

export type ConnectionState = 'connected' | 'reconnecting' | 'lost'

export interface PeerConfig {
  basePath: string                 // e.g. "/test/api/v1" (proxy)
  participantId: string
  displayName: string
  onLog?: (level: 'request' | 'response' | 'error' | 'info', text: string, detail?: string) => void
  // Fired when the peer transitions between connected / reconnecting / lost.
  // Consumers should render a "Reconnecting…" banner on 'reconnecting' and
  // tear down + offer a re-join on 'lost'.
  onConnectionStateChange?: (state: ConnectionState) => void
  // Informational — fired before each ICE restart attempt with the 1-indexed
  // attempt number (1, 2, 3 …). Consumers can surface this in dev tools.
  onIceRestart?: (attempt: number) => void
}

export interface InboundTrackStats {
  trackId: string
  kind: 'audio' | 'video' | string
  packetsReceived: number
  packetsLost: number
  jitter: number
  framesDecoded?: number
  frameWidth?: number
  frameHeight?: number
  framesPerSecond?: number
  bytesReceived: number
}

interface JoinResponse {
  data: {
    ws_token: string
    ice_servers: RTCIceServer[]
    session_id: string
    resumed: boolean
  }
}

interface PublishResponse {
  data: { session_description: RTCSessionDescriptionInit }
}

interface SubscribeResponse {
  data: { session_description: RTCSessionDescriptionInit }
}

interface RenegotiateResponse {
  data: { session_description: RTCSessionDescriptionInit }
}

export class TestPeer {
  // After 3 consecutive failed ICE restarts the peer gives up and emits
  // 'lost' so the consumer can prompt a full re-join.
  private static readonly MAX_ICE_RESTARTS = 3
  // Grace window after `disconnected` before triggering an ICE restart.
  // Many transient blips recover on their own within a few seconds.
  private static readonly DISCONNECTED_GRACE_MS = 5_000

  private cfg: PeerConfig
  private pc: RTCPeerConnection | null = null
  private localStream: MediaStream | null = null
  private screenStream: MediaStream | null = null
  private remoteStream: MediaStream = new MediaStream()
  private roomId = ''
  private published: Record<string, RTCRtpTransceiver> = {}
  private hasLeft = false

  // ── Connection-recovery state ─────────────────────────────────────
  private connectionState: ConnectionState = 'connected'
  private iceRestartAttempt = 0
  private iceRestartTimer: ReturnType<typeof setTimeout> | null = null

  // ── Resume support ────────────────────────────────────────────────
  // Tracked so the consumer can re-invoke joinRoom after a 'lost' event
  // with the prior session id, letting the server resume the same
  // Cloudflare session (see /api/v1/rooms/:id/join `resume_session_id`).
  // The server returns this in the join response; we cache it here.
  private lastSessionId: string | null = null

  // ── Page-unload cleanup ───────────────────────────────────────────
  private unloadHandler: (() => void) | null = null

  // ── Signaling WebSocket (presence heartbeat) ──────────────────────
  private ws: WebSocket | null = null
  private wsHeartbeatTimer: ReturnType<typeof setInterval> | null = null

  constructor(cfg: PeerConfig) {
    this.cfg = cfg
    this.installUnloadHooks()
  }

  // The session_id the server allocated for this peer's Cloudflare session.
  // Passed back as `resume_session_id` on a re-join after 'lost'.
  getSessionId(): string | null {
    return this.lastSessionId
  }

  getConnectionState(): ConnectionState {
    return this.connectionState
  }

  private setConnectionState(state: ConnectionState): void {
    if (this.connectionState === state) return
    this.connectionState = state
    this.cfg.onConnectionStateChange?.(state)
  }

  private clearIceRestartTimer(): void {
    if (this.iceRestartTimer) {
      clearTimeout(this.iceRestartTimer)
      this.iceRestartTimer = null
    }
  }

  // Performs a single ICE restart attempt against the same peer connection.
  // Server's /renegotiate endpoint accepts the SDP offer and returns the
  // upstream answer. After MAX_ICE_RESTARTS the peer gives up and emits
  // 'lost' so the consumer can prompt a full re-join.
  private async attemptIceRestart(): Promise<void> {
    this.clearIceRestartTimer()
    if (!this.pc || this.hasLeft) return

    if (this.iceRestartAttempt >= TestPeer.MAX_ICE_RESTARTS) {
      this.setConnectionState('lost')
      return
    }

    this.iceRestartAttempt += 1
    this.setConnectionState('reconnecting')
    this.cfg.onIceRestart?.(this.iceRestartAttempt)
    this.cfg.onLog?.('info', `ICE restart attempt ${this.iceRestartAttempt}`)

    try {
      const offer = await this.pc.createOffer({ iceRestart: true })
      await this.pc.setLocalDescription(offer)
      const res = await this.api<RenegotiateResponse>('POST', `/rooms/${encodeURIComponent(this.roomId)}/renegotiate`, {
        session_description: { type: offer.type, sdp: offer.sdp },
      })
      await this.pc.setRemoteDescription(res.data.session_description)
      // connectionstatechange → 'connected' will reset the attempt counter.
    } catch (e) {
      this.cfg.onLog?.('error', `ICE restart failed`, e instanceof Error ? e.message : String(e))
      // Schedule the next attempt, or escalate to 'lost' on the cap.
      if (this.iceRestartAttempt >= TestPeer.MAX_ICE_RESTARTS) {
        this.setConnectionState('lost')
      } else {
        this.iceRestartTimer = setTimeout(() => this.attemptIceRestart(), 2_000)
      }
    }
  }

  // Toggles the enabled flag on a local track. Used for mute/pause UX —
  // the track keeps flowing through the SFU but produces silence/black frames.
  setLocalTrackEnabled(kind: 'audio' | 'video', enabled: boolean): boolean {
    const stream = this.localStream ?? this.screenStream
    if (!stream) return false
    let changed = false
    for (const track of stream.getTracks()) {
      if (track.kind === kind && track.enabled !== enabled) {
        track.enabled = enabled
        changed = true
      }
    }
    return changed
  }

  // ── Lobby pre-warm ──────────────────────────────────────────────────
  // Fetches TURN credentials and creates the RTCPeerConnection ahead of
  // the join click. With media also pre-acquired, joinAndPublish() then
  // completes the entire join+publish in ONE request round trip — the
  // Agora preloadChannel / 100ms Preview pattern.
  async prepare(): Promise<void> {
    if (this.pc) return
    const res = await this.api<{ data: { ice_servers: RTCIceServer[] } }>('POST', '/credentials', {})
    this.createPeerConnection(res.data.ice_servers)
  }

  // Adopts a pre-acquired stream (lobby device warm-up) instead of calling
  // getUserMedia inside the connect path.
  useLocalStream(stream: MediaStream): void {
    this.localStream = stream
  }

  private createPeerConnection(iceServers: RTCIceServer[]): void {
    this.pc = new RTCPeerConnection({ iceServers, bundlePolicy: 'max-bundle' })

    this.pc.ontrack = (event) => {
      // Aggregate all remote tracks into a single MediaStream so a single
      // <video> element can render both the audio and video.
      this.remoteStream.addTrack(event.track)
      this.cfg.onLog?.('info', `peer "${this.cfg.participantId}" received remote ${event.track.kind} track`)
    }

    this.pc.onconnectionstatechange = () => {
      const cs = this.pc?.connectionState
      if (cs === 'connected') {
        this.iceRestartAttempt = 0
        this.clearIceRestartTimer()
        this.setConnectionState('connected')
      } else if (cs === 'disconnected') {
        // 5s grace — many transient blips recover without intervention.
        if (!this.iceRestartTimer) {
          this.iceRestartTimer = setTimeout(() => this.attemptIceRestart(), TestPeer.DISCONNECTED_GRACE_MS)
        }
      } else if (cs === 'failed') {
        this.clearIceRestartTimer()
        this.attemptIceRestart()
      }
    }
  }

  private joinBody(roomType: 'group' | '1-to-1'): Record<string, unknown> {
    const body: Record<string, unknown> = {
      display_name: this.cfg.displayName,
      room_type: roomType,
      participant_type: 'publisher',
    }
    // Reuse the prior session if the consumer is reconnecting after 'lost'.
    if (this.lastSessionId) body.resume_session_id = this.lastSessionId
    return body
  }

  async joinRoom(roomId: string, roomType: 'group' | '1-to-1' = 'group'): Promise<void> {
    this.roomId = roomId
    const res = await this.api<JoinResponse>('POST', `/rooms/${encodeURIComponent(roomId)}/join`, this.joinBody(roomType))

    this.lastSessionId = res.data.session_id

    // A prepared peer (lobby pre-warm) already has its PeerConnection.
    if (!this.pc) this.createPeerConnection(res.data.ice_servers)
    this.connectSignaling(roomId, res.data.ws_token)
  }

  // Connects the room's signaling WebSocket (AnyCable, ActionCable wire
  // protocol) with the ws_token from /join. This maintains server-side
  // presence: without a heartbeat the cleanup worker evicts the participant
  // once the join grace lapses. Failures are NON-FATAL — REST signaling also
  // refreshes liveness, and dev environments may not run anycable-go.
  private connectSignaling(roomId: string, wsToken: string): void {
    if (typeof WebSocket === 'undefined' || !wsToken || this.ws) return

    const proto = window.location.protocol === 'https:' ? 'wss' : 'ws'
    const url = `${proto}://${window.location.host}/cable?token=${encodeURIComponent(wsToken)}`
    const identifier = JSON.stringify({ channel: 'RoomChannel', room_id: roomId })

    let ws: WebSocket
    try {
      ws = new WebSocket(url)
    } catch {
      return
    }
    this.ws = ws

    ws.onopen = () => ws.send(JSON.stringify({ command: 'subscribe', identifier }))
    ws.onmessage = (event) => {
      let msg: { type?: string; identifier?: string; message?: { type?: string } }
      try {
        msg = JSON.parse(String(event.data))
      } catch {
        return
      }
      if (msg.type === 'confirm_subscription') {
        this.cfg.onLog?.('info', `signaling WS connected (presence heartbeat every 20s)`)
        this.wsHeartbeatTimer = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ command: 'message', identifier, data: JSON.stringify({ action: 'heartbeat' }) }))
          }
        }, 20_000)
      } else if (msg.type === 'reject_subscription') {
        this.cfg.onLog?.('error', 'signaling WS subscription rejected')
      } else if (msg.message?.type && msg.message.type !== 'heartbeat_ack') {
        this.cfg.onLog?.('info', `signal: ${msg.message.type}`)
      }
    }
    ws.onclose = () => this.teardownSignaling()
    ws.onerror = () => {
      this.cfg.onLog?.('info', 'signaling WS unavailable — falling back to REST liveness')
    }
  }

  private teardownSignaling(): void {
    if (this.wsHeartbeatTimer) {
      clearInterval(this.wsHeartbeatTimer)
      this.wsHeartbeatTimer = null
    }
    if (this.ws) {
      const ws = this.ws
      this.ws = null
      ws.onclose = null
      try {
        ws.close()
      } catch {
        /* already closed */
      }
    }
  }

  // Single round trip: join + publish in one request. Requires prepare()
  // and a local stream (useLocalStream or getUserMedia) beforehand.
  async joinAndPublish(
    roomId: string,
    names: { audio?: string; video?: string; screen_video?: string },
    roomType: 'group' | '1-to-1' = 'group'
  ): Promise<void> {
    if (!this.pc) throw new Error('call prepare() before joinAndPublish')
    this.roomId = roomId

    const inputs = this.addPublishTransceivers(names)
    const offer = await this.pc.createOffer()
    await this.pc.setLocalDescription(offer)
    for (const i of inputs) {
      const trans = this.published[i.track_name]
      if (trans?.mid) i.mid = trans.mid
    }

    const res = await this.api<JoinResponse & { data: { publish: { session_description: RTCSessionDescriptionInit } } }>(
      'POST',
      `/rooms/${encodeURIComponent(roomId)}/join`,
      {
        ...this.joinBody(roomType),
        tracks: inputs,
        session_description: { type: offer.type, sdp: offer.sdp },
      }
    )

    this.lastSessionId = res.data.session_id
    this.connectSignaling(roomId, res.data.ws_token)
    await this.pc.setRemoteDescription(res.data.publish.session_description)
  }

  async getUserMedia(constraints: { audio?: boolean; video?: boolean }): Promise<MediaStream> {
    this.localStream = await navigator.mediaDevices.getUserMedia({
      audio: constraints.audio ?? false,
      video: constraints.video ?? false,
    })
    return this.localStream
  }

  async getDisplayMedia(): Promise<MediaStream> {
    this.screenStream = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: false })
    return this.screenStream
  }

  getLocalStream(): MediaStream | null {
    return this.localStream ?? this.screenStream
  }

  getRemoteStream(): MediaStream {
    return this.remoteStream
  }

  private addPublishTransceivers(names: { audio?: string; video?: string; screen_video?: string }): { track_name: string; kind: 'audio' | 'video'; mid: string }[] {
    if (!this.pc) throw new Error('not joined')

    const inputs: { track_name: string; kind: 'audio' | 'video'; mid: string }[] = []

    if (this.localStream && names.audio) {
      const t = this.localStream.getAudioTracks()[0]
      if (t) {
        const trans = this.pc.addTransceiver(t, { direction: 'sendonly' })
        this.published[names.audio] = trans
        inputs.push({ track_name: names.audio, kind: 'audio', mid: '' })
      }
    }
    if (this.localStream && names.video) {
      const t = this.localStream.getVideoTracks()[0]
      if (t) {
        const trans = this.pc.addTransceiver(t, { direction: 'sendonly' })
        this.published[names.video] = trans
        inputs.push({ track_name: names.video, kind: 'video', mid: '' })
      }
    }
    if (this.screenStream && names.screen_video) {
      const t = this.screenStream.getVideoTracks()[0]
      if (t) {
        const trans = this.pc.addTransceiver(t, { direction: 'sendonly' })
        this.published[names.screen_video] = trans
        inputs.push({ track_name: names.screen_video, kind: 'video', mid: '' })
      }
    }

    return inputs
  }

  async publish(names: { audio?: string; video?: string; screen_video?: string }): Promise<void> {
    if (!this.pc) throw new Error('not joined')

    const inputs = this.addPublishTransceivers(names)

    // mids are populated only after setLocalDescription.
    const offer = await this.pc.createOffer()
    await this.pc.setLocalDescription(offer)
    for (const i of inputs) {
      const trans = this.published[i.track_name]
      if (trans?.mid) i.mid = trans.mid
    }

    const res = await this.api<PublishResponse>('POST', `/rooms/${encodeURIComponent(this.roomId)}/publish`, {
      tracks: inputs,
      session_description: { type: offer.type, sdp: offer.sdp },
    })
    await this.pc.setRemoteDescription(res.data.session_description)
  }

  // Resolves once every named published track is actually sending RTP
  // (outbound-rtp bytesSent > 0). Cloudflare's SFU rejects subscribers who
  // pull a track before the publisher's media is flowing (empty_track_error),
  // so a track must not be announced as pullable before this point. Mirrors
  // Cloudflare's partytracks client, which gates track metadata on bytesSent.
  // Resolves true when live, false on timeout (callers may proceed and rely
  // on subscriber-side tracks_not_ready retries as the fallback).
  async waitForTracksLive(trackNames?: string[], timeoutMs = 3_000): Promise<boolean> {
    if (!this.pc) throw new Error('not joined')
    const names = trackNames ?? Object.keys(this.published)
    const senders = names
      .map((n) => this.published[n]?.sender)
      .filter((s): s is RTCRtpSender => !!s)
    if (senders.length === 0) return true

    const deadline = performance.now() + timeoutMs
    let delay = 10
    for (;;) {
      const liveChecks = await Promise.all(
        senders.map(async (sender) => {
          const stats = await sender.getStats()
          let live = false
          stats.forEach((s) => {
            const r = s as { type?: string; bytesSent?: number }
            if (r.type === 'outbound-rtp' && (r.bytesSent ?? 0) > 0) live = true
          })
          return live
        })
      )
      if (liveChecks.every(Boolean)) return true
      if (performance.now() >= deadline) return false
      await new Promise((r) => setTimeout(r, delay))
      delay = Math.min(delay * 2, 100)
    }
  }

  // Waits until the published tracks are actually sending RTP, then
  // announces them to the room via /publish/confirm — the server fires the
  // track_published broadcast there, so subscribers reacting to it can pull
  // immediately without hitting tracks_not_ready.
  async confirmPublished(trackNames?: string[]): Promise<void> {
    const names = trackNames ?? Object.keys(this.published)
    if (names.length === 0) return
    await this.waitForTracksLive(names)
    await this.api('POST', `/rooms/${encodeURIComponent(this.roomId)}/publish/confirm`, {
      track_names: names,
    })
  }

  async subscribeTo(
    subscriptions: { participant_id: string; track_names: string[]; track_kinds?: ('audio' | 'video')[] }[]
  ): Promise<void> {
    if (!this.pc) throw new Error('not joined')

    // Fast path (single round trip): when track kinds are known we pre-add
    // recvonly transceivers and send our own offer with the subscribe request
    // (Cloudflare's WHEP pattern). The response is then an answer, so the
    // extra /subscribe/answer round trip disappears.
    const allKindsKnown = subscriptions.every(
      (s) => s.track_kinds && s.track_kinds.length === s.track_names.length
    )
    if (allKindsKnown) {
      for (const sub of subscriptions) {
        for (const kind of sub.track_kinds!) {
          const trans = this.pc.addTransceiver(kind, { direction: 'recvonly' })
          // Cloudflare's SFU mishandles dual-profile H265 offers: its answer
          // drops one H265 payload but keeps that payload's RTX entry, whose
          // dangling apt= reference makes Chrome reject setRemoteDescription
          // ("Failed to set remote video description send parameters").
          // Excluding H265 from the recvonly offer avoids the broken answer.
          if (kind === 'video' && typeof trans.setCodecPreferences === 'function') {
            const caps = RTCRtpReceiver.getCapabilities('video')
            const codecs = caps?.codecs.filter((c) => !/h265|hevc/i.test(c.mimeType)) ?? []
            if (codecs.length > 0) trans.setCodecPreferences(codecs)
          }
        }
      }
      const offer = await this.pc.createOffer()
      await this.pc.setLocalDescription(offer)
      const res = await this.api<SubscribeResponse>('POST', `/rooms/${encodeURIComponent(this.roomId)}/subscribe`, {
        subscriptions: subscriptions.map(({ participant_id, track_names }) => ({ participant_id, track_names })),
        session_description: { type: offer.type, sdp: offer.sdp },
      })
      await this.pc.setRemoteDescription(res.data.session_description)
      return
    }

    // Legacy path: the server relays Cloudflare's offer; we answer and post
    // it back via /subscribe/answer (two round trips).
    const res = await this.api<SubscribeResponse>('POST', `/rooms/${encodeURIComponent(this.roomId)}/subscribe`, {
      subscriptions,
    })
    await this.pc.setRemoteDescription(res.data.session_description)

    const answer = await this.pc.createAnswer()
    await this.pc.setLocalDescription(answer)

    await this.api('POST', `/rooms/${encodeURIComponent(this.roomId)}/subscribe/answer`, {
      session_description: { type: answer.type, sdp: answer.sdp },
    })
  }

  // Returns the RTT (ms) of the selected ICE candidate pair. Cloudflare's
  // SFU edge is the remote endpoint, so this is the round-trip latency a
  // real user experiences to the nearest CF PoP.
  async getRtt(): Promise<{ rttMs: number | null; localCandidate?: string; remoteCandidate?: string }> {
    if (!this.pc) return { rttMs: null }

    interface CandidatePairStat {
      id: string
      type: 'candidate-pair'
      currentRoundTripTime?: number
      localCandidateId?: string
      remoteCandidateId?: string
      selected?: boolean
      nominated?: boolean
      state?: string
    }
    interface CandidateStat {
      id: string
      candidateType?: string
      protocol?: string
      address?: string
      ip?: string
      port?: number
    }

    const stats = await this.pc.getStats()
    let selected: CandidatePairStat | undefined
    const candidatesById: Record<string, CandidateStat> = {}

    stats.forEach((raw) => {
      const s = raw as { type: string }
      if (s.type === 'local-candidate' || s.type === 'remote-candidate') {
        const c = raw as CandidateStat
        candidatesById[c.id] = c
      }
      if (s.type === 'candidate-pair') {
        const p = raw as CandidatePairStat
        if (p.selected || p.nominated) {
          if (!selected || p.selected) selected = p
        }
      }
    })

    if (!selected) return { rttMs: null }
    const rttMs = typeof selected.currentRoundTripTime === 'number' ? Math.round(selected.currentRoundTripTime * 1000) : null
    const local = selected.localCandidateId ? candidatesById[selected.localCandidateId] : undefined
    const remote = selected.remoteCandidateId ? candidatesById[selected.remoteCandidateId] : undefined
    return {
      rttMs,
      localCandidate: local ? `${local.candidateType ?? '?'} ${local.protocol ?? ''}`.trim() : undefined,
      remoteCandidate: remote ? `${remote.candidateType ?? '?'} ${remote.protocol ?? ''} ${remote.address ?? remote.ip ?? ''}`.trim() : undefined,
    }
  }

  // Resolves when ICE connection state reaches 'connected' (or 'completed').
  async waitForIceConnected(timeoutMs = 15_000): Promise<void> {
    if (!this.pc) throw new Error('not joined')
    const isConnected = (state: string | undefined) => state === 'connected' || state === 'completed'
    if (isConnected(this.pc.connectionState)) return
    const deadline = Date.now() + timeoutMs
    return new Promise<void>((resolve, reject) => {
      const pc = this.pc!
      const check = () => {
        if (!this.pc) return reject(new Error('peer closed'))
        if (isConnected(this.pc.connectionState)) {
          pc.onconnectionstatechange = null
          resolve()
        } else if (Date.now() > deadline) {
          pc.onconnectionstatechange = null
          reject(new Error(`ICE did not reach 'connected' within ${timeoutMs}ms (state=${this.pc.connectionState})`))
        }
      }
      pc.onconnectionstatechange = check
      check()
    })
  }

  async getInboundStats(): Promise<InboundTrackStats[]> {
    if (!this.pc) return []
    const stats = await this.pc.getStats()
    const out: InboundTrackStats[] = []
    stats.forEach((s) => {
      if (s.type === 'inbound-rtp') {
        const r = s as RTCInboundRtpStreamStats & {
          framesDecoded?: number
          frameWidth?: number
          frameHeight?: number
          framesPerSecond?: number
        }
        out.push({
          trackId: r.trackIdentifier ?? '',
          kind: r.kind ?? '',
          packetsReceived: r.packetsReceived ?? 0,
          packetsLost: r.packetsLost ?? 0,
          jitter: r.jitter ?? 0,
          framesDecoded: r.framesDecoded,
          frameWidth: r.frameWidth,
          frameHeight: r.frameHeight,
          framesPerSecond: r.framesPerSecond,
          bytesReceived: r.bytesReceived ?? 0,
        })
      }
    })
    return out
  }

  async leave(): Promise<void> {
    if (this.hasLeft) return
    this.hasLeft = true
    this.clearIceRestartTimer()
    this.uninstallUnloadHooks()
    try {
      // REST leave first (clean "voluntary" departure); the WS unsubscribe
      // that follows then finds the participant already gone and no-ops.
      await this.api('POST', `/rooms/${encodeURIComponent(this.roomId)}/leave`, undefined)
    } catch {
      /* best effort */
    }
    this.teardownSignaling()
    this.pc?.close()
    this.localStream?.getTracks().forEach((t) => t.stop())
    this.screenStream?.getTracks().forEach((t) => t.stop())
    this.remoteStream.getTracks().forEach((t) => t.stop())
    this.pc = null
    this.localStream = null
    this.screenStream = null
    this.remoteStream = new MediaStream()
  }

  // Atomically swap the underlying MediaStreamTrack on a published
  // transceiver — typically used when the user picks a different camera
  // or microphone. No SDP renegotiation is required when the codec is
  // unchanged; the SFU sees the same RTP stream identifier and the
  // change is invisible end-to-end.
  //
  // For codec changes (e.g. webcam → screen-share), unpublish + publish
  // is still required because Cloudflare's SFU pins the codec to the
  // first track on a given trackName.
  async replaceTrack(trackName: string, newTrack: MediaStreamTrack): Promise<boolean> {
    const trans = this.published[trackName]
    if (!trans?.sender) return false
    await trans.sender.replaceTrack(newTrack)
    return true
  }

  // Page-unload hooks. We register both `pagehide` and `beforeunload`
  // because Safari/iOS does not fire `beforeunload` consistently. The
  // `keepalive` flag on `fetch` lets the request complete during teardown.
  private installUnloadHooks(): void {
    if (typeof window === 'undefined' || this.unloadHandler) return
    this.unloadHandler = () => {
      if (this.hasLeft || !this.roomId) return
      try {
        fetch(`${this.cfg.basePath}/rooms/${encodeURIComponent(this.roomId)}/leave`, {
          method: 'POST',
          headers: {
            'Accept': 'application/json',
            'X-Participant-Id': this.cfg.participantId,
          },
          keepalive: true,
        })
      } catch {
        /* best effort — page is going away */
      }
    }
    window.addEventListener('pagehide', this.unloadHandler)
    window.addEventListener('beforeunload', this.unloadHandler)
  }

  private uninstallUnloadHooks(): void {
    if (typeof window === 'undefined' || !this.unloadHandler) return
    window.removeEventListener('pagehide', this.unloadHandler)
    window.removeEventListener('beforeunload', this.unloadHandler)
    this.unloadHandler = null
  }

  private async api<T = unknown>(method: string, path: string, body?: unknown): Promise<T> {
    const url = `${this.cfg.basePath}${path}`
    const headers: Record<string, string> = {
      Accept: 'application/json',
      'X-Participant-Id': this.cfg.participantId,
    }
    if (body !== undefined) headers['Content-Type'] = 'application/json'

    // Retries:
    //  - 502 once (transient Cloudflare Calls upstream errors are common)
    //  - 503 tracks_not_ready up to maxAttempts, honoring the server's
    //    Retry-After-Ms hint (the publisher's media hasn't propagated yet)
    // Everything else (4xx, 500, 504) is terminal.
    const maxAttempts = 5
    let retried502 = false
    let lastErr: Error | undefined
    this.cfg.onLog?.('request', `${method} ${path}`, body !== undefined ? JSON.stringify(body, null, 2) : undefined)
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      const t0 = performance.now()
      const response = await fetch(url, {
        method,
        headers,
        body: body === undefined ? undefined : JSON.stringify(body),
      })
      const text = await response.text()
      const ms = Math.round(performance.now() - t0)
      let parsed: unknown
      try {
        parsed = text.length > 0 ? JSON.parse(text) : null
      } catch {
        parsed = text
      }
      this.cfg.onLog?.(response.ok ? 'response' : 'error', `${response.status} ${response.statusText} — ${method} ${path} in ${ms}ms`)
      if (response.ok) return parsed as T

      const errBody = typeof parsed === 'object' && parsed !== null ? (parsed as { detail?: unknown; code?: unknown }) : {}
      const detail = errBody.detail !== undefined ? String(errBody.detail) : text
      lastErr = new Error(`${method} ${path} → ${response.status}: ${detail}`)

      if (response.status === 502 && !retried502) {
        retried502 = true
        this.cfg.onLog?.('info', `retry ${method} ${path} after 502`)
        await new Promise((r) => setTimeout(r, 250))
        continue
      }
      if (response.status === 503 && errBody.code === 'tracks_not_ready' && attempt < maxAttempts) {
        const retryMs = Number(response.headers.get('Retry-After-Ms')) || 250
        this.cfg.onLog?.('info', `retry ${method} ${path} in ${retryMs}ms (tracks_not_ready, attempt ${attempt}/${maxAttempts})`)
        await new Promise((r) => setTimeout(r, retryMs))
        continue
      }
      throw lastErr
    }
    throw lastErr ?? new Error(`${method} ${path} failed after retries`)
  }
}

export async function waitFor<T>(
  fn: () => Promise<T>,
  predicate: (value: T) => boolean,
  opts: { timeoutMs?: number; intervalMs?: number; label?: string } = {}
): Promise<T> {
  const timeoutMs = opts.timeoutMs ?? 10_000
  const intervalMs = opts.intervalMs ?? 500
  const deadline = Date.now() + timeoutMs
  let last: T | undefined
  while (Date.now() < deadline) {
    last = await fn()
    if (predicate(last)) return last
    await new Promise((r) => setTimeout(r, intervalMs))
  }
  throw new Error(`waitFor "${opts.label ?? 'unnamed'}" timed out after ${timeoutMs}ms (last: ${JSON.stringify(last)})`)
}
